import * as React from 'react';
import { TextField, Button, NumberField } from '../common';
import { Grid2 as Grid } from '@mui/material';
import { AddOutlined, DeleteOutlined } from '@mui/icons-material';
import { BaseDialog, BaseDialogProps, BaseDialogState } from '../common/dialog/BaseDialog';

interface Props extends BaseDialogProps {
    readonly onCreate: (achievement: Entities.Achievement) => void;
}

interface State extends BaseDialogState {
    readonly id: string;
    readonly metadataId: string;
    readonly appleId: string;
    readonly googleId: string;
    readonly prizes: Entities.AchievementPrize[];
}

export class CreateAchievementDialog extends BaseDialog<Props, State> {
    state = this.initState();

    protected renderContent(): React.JSX.Element {
        const { id, metadataId, appleId, googleId, prizes } = this.state;
        const prizeElements: React.JSX.Element[] = [];
        prizes.forEach( (prize, prizeIndex) => prizeElements.push(this.renderPrize(prize, prizeIndex)));

        return (
            <Grid container={true} spacing={0}>
                <Grid size={12}>
                    <TextField
                        label={'Id'}
                        fullWidth={true}
                        value={id}
                        required={true}
                        onChange={this.updateId}
                        variant="outlined"
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        label={'Metadata Id'}
                        fullWidth={true}
                        value={metadataId}
                        required={true}
                        onChange={this.updateMetadataId}
                        variant="outlined"
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        label={'Apple Id'}
                        fullWidth={true}
                        value={appleId}
                        required={true}
                        onChange={this.updateAppleId}
                        variant="outlined"
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        label={'Google Id'}
                        fullWidth={true}
                        value={googleId}
                        required={true}
                        onChange={this.updateGoogleId}
                        variant="outlined"
                    />
                </Grid>
                <Grid size={12} style={{ padding: '8px 0px'}}/>
                <Grid size={12}>
                    <Grid container={true} spacing={0}>
                        <Grid size={12}>
                            <strong>PRIZES</strong>
                        </Grid>
                        <Grid size={12}>
                            <Grid container={true} spacing={1} style={{backgroundColor: 'white', margin: 0, width: '100%'}}>
                                <>
                                    {prizes.length === 0 && (
                                        <div style={{padding: 10}}>
                                            No prizes
                                        </div>
                                    )}
                                    {prizeElements}
                                </>
                            </Grid>
                        </Grid>
                        <Grid size={12} style={{ padding: '8px 0px' }}>
                            <Button text="Add Prize" icon={AddOutlined} variant="contained" onClick={this.onAddReward} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    protected renderActions(): React.JSX.Element {
        return (
            <Button text="Create" onClick={this.onSubmit} variant="text" color="secondary" disabled={!this.validate()} />
        );
    }

    private onAddReward = () => {
        const { prizes } = this.state;
        prizes.push({
            id: 0,
            achievementId: '',
            key: '',
            amount: 0,
        });
        this.setState({ prizes });
    };

    private renderPrize = (prize: Entities.AchievementPrize, prizeIndex: number) => {
        let { prizes } = this.state;

        const onKeyChange = (value: string) => {
            prizes[prizeIndex].key = value;
            this.setState({ prizes });
        };

        const onAmountChange = (value: number) => {
            prizes[prizeIndex].amount = value;
            this.setState({ prizes });
        };

        const onRemovePrize = () => {
            prizes = prizes.filter((value, i) => i !== prizeIndex);
            this.setState({ prizes });
        };

        return (
            <Grid size={12}>
                <Grid container={true} spacing={0} style={{borderBottom: '1px solid rgb(233, 233, 233)', margin: 0, width: '100%'}}>
                    <Grid size={12}>
                        <TextField
                            label={'Key'}
                            fullWidth={true}
                            value={prize.key}
                            onChange={onKeyChange}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid size={12}>
                        <NumberField
                            label={'Amount'}
                            fullWidth={true}
                            value={prize.amount}
                            onChange={onAmountChange}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid size={12} style={{textAlign: 'end', padding: '8px 0px'}}>
                        <Button text="Remove Prize" icon={DeleteOutlined} variant="contained" onClick={onRemovePrize} />
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    protected initState(): State {
        return {
            id: '',
            metadataId: '',
            appleId: '',
            googleId: '',
            prizes: [],
        };
    }

    private updateId = (id: string) => this.setState({id});

    private updateMetadataId = (metadataId: string) => this.setState({metadataId});

    private updateAppleId = (appleId: string) => this.setState({appleId});

    private updateGoogleId = (googleId: string) => this.setState({googleId});

    private validate(): boolean {
        return true;
    }

    private onSubmit = () => {
        const { id, metadataId, appleId, googleId, prizes } = this.state;
        if (!this.validate()) {
            return;
        }
        const achievement: Entities.Achievement = {
            id,
            metadataId,
            appleId,
            googleId,
            updated: 0,
            prizes,
        };
        this.props.onCreate(achievement);
        this.onClose();
    };
}
